// src/pages/Talleres/DetalleTaller.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Result, Button, Alert } from 'antd';
import { ClockCircleOutlined, StopOutlined, InfoCircleOutlined, SafetyCertificateOutlined, IdcardOutlined } from '@ant-design/icons';

import TallerBanner from './TallerBanner';
import TallerInfo from './TallerInfo';
import FormularioInscripcion from './FormularioInscripcion';
import SinAutenticacion from './SinAutenticacion';

import { useTallerData } from '../../../hooks/useTalleresData';
import { useInscripcionForm } from '../../../hooks/useInscripcionForm';
import { useEstadoInscripcion } from '../../../hooks/useEstadoInscripcion';

import Auth from '../../../service/Auth';
import Familiares from '../../../service/Familiares';
import TarjetaVecino from '../../../service/tarjetaVecino';
import MatriculaService from '../../../service/Matriculas';

export default function DetalleTaller() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { taller, loading: loadingTaller, esGratis } = useTallerData(id);

  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [familiares, setFamiliares] = useState([]);
  const [loadingFamiliares, setLoadingFamiliares] = useState(false);

  // Hora del servidor — bloquea el formulario hasta tener respuesta concreta
  const [serverTime, setServerTime] = useState(null);
  const [loadingServerTime, setLoadingServerTime] = useState(true);
  const [errorServerTime, setErrorServerTime] = useState(false);

  // Estados para validación de Tarjeta Vecino
  const [validandoTarjetaVecino, setValidandoTarjetaVecino] = useState(false);
  const [esBeneficiarioTarjetaVecino, setEsBeneficiarioTarjetaVecino] = useState(null);
  const [errorTarjetaVecino, setErrorTarjetaVecino] = useState(null);

  // Configuración de matrícula del recinto (solo informativa: se suma al pagar)
  const [validandoMatricula, setValidandoMatricula] = useState(false);
  const [infoMatricula, setInfoMatricula] = useState(null);

  const inscripcionForm = useInscripcionForm(esGratis);

  const estadoInscripcion = useEstadoInscripcion(taller, serverTime);

  // ✅ Obtener hora del servidor — sin fallback, bloquea hasta tener respuesta
  useEffect(() => {
    const fetchServerTime = async () => {
      try {
        setLoadingServerTime(true);
        setErrorServerTime(false);
        const time = await Auth.getServerTime();
        console.log('⏰ Hora del servidor obtenida:', time);
        setServerTime(time);
      } catch (error) {
        console.error('❌ No se pudo obtener hora del servidor:', error.message);
        setErrorServerTime(true);
      } finally {
        setLoadingServerTime(false);
      }
    };
    fetchServerTime();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoadingAuth(true);
        const res = await Auth.Me();
        setUser({
          sub: res.id || res.sub,
          id: res.id,
          name: res.name || res.dynamoProfile?.name,
          email: res.email,
          phone: res.phone || res.dynamoProfile?.phone,
          rut: res.rut || res.dynamoProfile?.rut,
          birthdate: res.birthdate || res.dynamoProfile?.birthdate,
          role: res.role,
          gender: res.gender || res.dynamoProfile?.gender
        });
      } catch (error) {
        console.error('Error obteniendo usuario:', error);
        setUser(null);
      } finally {
        setLoadingAuth(false);
      }
    };
    loadUserData();
  }, []);

  useEffect(() => {
    const loadFamiliares = async () => {
      if (!user) return;
      try {
        setLoadingFamiliares(true);
        const res = await Familiares.List();
        setFamiliares(res.data || res || []);
      } catch (error) {
        console.error('Error cargando familiares:', error);
      } finally {
        setLoadingFamiliares(false);
      }
    };
    loadFamiliares();
  }, [user]);

  useEffect(() => {
    const validarTarjetaVecino = async () => {
      if (!taller?.soloTarjetaVecino || !user?.rut) {
        setEsBeneficiarioTarjetaVecino(true);
        return;
      }
      try {
        setValidandoTarjetaVecino(true);
        setErrorTarjetaVecino(null);
        const response = await TarjetaVecino.Validar(user.rut);
        if (response.estado === 'VIGENTE' && response.esVecino) {
          setEsBeneficiarioTarjetaVecino(true);
        } else {
          setEsBeneficiarioTarjetaVecino(false);
        }
      } catch (error) {
        console.error('Error validando Tarjeta Vecino:', error);
        setErrorTarjetaVecino(error.message || 'Error al validar Tarjeta Vecino');
        setEsBeneficiarioTarjetaVecino(false);
      } finally {
        setValidandoTarjetaVecino(false);
      }
    };
    validarTarjetaVecino();
  }, [taller?.soloTarjetaVecino, user?.rut]);

  // Matrícula: ya no bloquea la inscripción. Si el producto la exige y la persona
  // inscrita no tiene una vigente, se suma sola al pagar (oct-2026). Acá solo se
  // carga la configuración del recinto para avisar cuánto cuesta; el estado de la
  // persona inscrita se muestra en el formulario, porque puede ser un familiar.
  // Antes se miraba la matrícula del titular de la cuenta y se pedía comprarla
  // aparte, incluso con la matrícula fuera de venta.
  useEffect(() => {
    const cargarMatricula = async () => {
      if (!taller?.requiereMatricula || !taller?.recinto) {
        setInfoMatricula(null);
        return;
      }
      try {
        setValidandoMatricula(true);
        const r = await MatriculaService.VerificarConfiguracionRecinto(taller.recinto._id || taller.recinto);
        setInfoMatricula(r?.tieneConfiguracion ? r.configuracion : null);
      } catch {
        setInfoMatricula(null);
      } finally {
        setValidandoMatricula(false);
      }
    };
    cargarMatricula();
  }, [taller?.requiereMatricula, taller?.recinto]);

  // ── Guards de carga ──────────────────────────────────────────────────────────

  if (loadingTaller || loadingAuth || loadingServerTime) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spin size="large" tip="Cargando..." />
      </div>
    );
  }

  if (!taller) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Result
          status="404"
          title="Taller no encontrado"
          subTitle="El taller que buscas no existe o ha sido eliminado"
          extra={
            <Button type="primary" onClick={() => navigate('/talleres')}>
              Volver a talleres
            </Button>
          }
        />
      </div>
    );
  }

  // ✅ Si el servidor no respondió, bloquear completamente — nunca usar hora local
  if (errorServerTime) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Result
          status="500"
          title="Error de conexión"
          subTitle="No se pudo verificar la disponibilidad del taller. Por favor, recarga la página."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  // ── Lógica de acceso ─────────────────────────────────────────────────────────

  // La matrícula ya no es requisito para ver el formulario: se suma al pagar.
  const cumpleRequisitosAcceso =
    (!taller.soloTarjetaVecino || esBeneficiarioTarjetaVecino);

  const clp = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;
  const valoresMatricula = infoMatricula && [
    ['No vecino', infoMatricula.valores?.particular ?? infoMatricula.valorMatricula],
    ['Vecino', infoMatricula.valores?.vecino],
    ['Adulto mayor', infoMatricula.valores?.adultoMayor],
    ['Funcionario', infoMatricula.valores?.funcionario],
  ].filter(([, v]) => v !== null && v !== undefined);

  return (
    <div className="min-h-screen">
      <TallerBanner taller={taller} esGratis={esGratis} />

      <div className="max-w-7xl mx-auto p-4 md:p-8 -mt-20 relative z-10">
        <TallerInfo taller={taller} esGratis={esGratis} />

        {/* Alerta de Tarjeta Vecino requerida */}
        {taller.soloTarjetaVecino && user?.sub && (
          <>
            {validandoTarjetaVecino ? (
              <Alert
                message="Verificando beneficio..."
                description="Estamos validando tu estado como beneficiario de Tarjeta Vecino"
                type="info"
                showIcon
                icon={<Spin size="small" />}
                className="mb-6"
              />
            ) : esBeneficiarioTarjetaVecino === false ? (
              <Alert
                message="Taller exclusivo para beneficiarios de Tarjeta Vecino"
                description={
                  <div className="space-y-2">
                    <p>Este taller está disponible únicamente para beneficiarios activos de Tarjeta Vecino.</p>
                    {errorTarjetaVecino && (
                      <p className="text-sm text-red-600">{errorTarjetaVecino}</p>
                    )}
                    <p className="text-sm">Si crees que esto es un error, por favor contacta con la administración municipal.</p>
                  </div>
                }
                type="error"
                showIcon
                icon={<SafetyCertificateOutlined />}
                className="mb-6"
              />
            ) : esBeneficiarioTarjetaVecino === true ? (
              <Alert
                message="✓ Beneficiario de Tarjeta Vecino verificado"
                description="Tu estado como beneficiario ha sido verificado correctamente. Puedes continuar con la inscripción."
                type="success"
                showIcon
                icon={<SafetyCertificateOutlined />}
                className="mb-6"
              />
            ) : null}
          </>
        )}

        {/* Matrícula del recinto: se avisa cuánto cuesta y que se suma al pagar. Si la
            matrícula no está a la venta no hay configuración y no se muestra nada. */}
        {taller.requiereMatricula && !validandoMatricula && infoMatricula && (
          <Alert
            message="Este producto requiere matrícula del recinto"
            description={
              <div className="space-y-2">
                <p>
                  Si la persona inscrita no tiene matrícula vigente, <strong>se suma automáticamente al pagar</strong>.
                  Si ya la tiene, la compra sigue normal. En el último paso te mostramos su caso.
                </p>
                <p className="text-sm">
                  Matrícula {infoMatricula.textoVigencia ? `(${infoMatricula.textoVigencia.toLowerCase()})` : ''}:{' '}
                  {valoresMatricula.map(([l, v]) => `${l} ${v > 0 ? clp(v) : 'gratis'}`).join(' · ')}
                </p>
                <p className="text-xs text-gray-500">Vence si pasan 2 años sin compras en el recinto.</p>
              </div>
            }
            type="info"
            showIcon
            icon={<IdcardOutlined />}
            className="mb-6"
          />
        )}

        {/* Alerta de estado de inscripción */}
        {estadoInscripcion.mensaje && (
          <Alert
            message={
              estadoInscripcion.tipo === 'proximo' ? 'Inscripciones Próximas' :
              estadoInscripcion.tipo === 'cerrado' ? 'Inscripciones Cerradas' :
              'Inscripciones No Disponibles'
            }
            description={estadoInscripcion.mensaje}
            type={
              estadoInscripcion.tipo === 'proximo' ? 'info' :
              estadoInscripcion.tipo === 'cerrado' ? 'warning' :
              'error'
            }
            icon={
              estadoInscripcion.tipo === 'proximo' ? <ClockCircleOutlined /> :
              estadoInscripcion.tipo === 'cerrado' ? <StopOutlined /> :
              <InfoCircleOutlined />
            }
            showIcon
            className="mb-6"
          />
        )}

        {/* Solo mostrar formulario si cumple todos los requisitos */}
        {estadoInscripcion.mostrarFormulario && cumpleRequisitosAcceso && (
          <>
            {user?.sub ? (
              <FormularioInscripcion
                tallerId={id}
                taller={taller}
                esGratis={esGratis}
                user={user}
                familiares={familiares}
                loadingFamiliares={loadingFamiliares}
                inscripcionForm={inscripcionForm}
                infoMatricula={infoMatricula}
                estadoInscripcion={estadoInscripcion}
              />
            ) : (
              <SinAutenticacion taller={taller} esGratis={esGratis} />
            )}
          </>
        )}

        {/* Debug (remover en producción) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-gray-100 rounded text-xs">
            <strong>Debug:</strong> Estado: {estadoInscripcion.tipo} |
            Mostrar formulario: {estadoInscripcion.mostrarFormulario ? 'Sí' : 'No'} |
            Server time: {serverTime?.toISOString() ?? 'Cargando...'} |
            Requiere TV: {taller.soloTarjetaVecino ? 'Sí' : 'No'} |
            Es beneficiario: {esBeneficiarioTarjetaVecino === null ? 'Verificando...' : esBeneficiarioTarjetaVecino ? 'Sí' : 'No'} |
            Requiere matrícula: {taller.requiereMatricula ? 'Sí' : 'No'} |
            Matrícula en venta: {infoMatricula ? 'Sí' : 'No'}
          </div>
        )}
      </div>
    </div>
  );
}
